/**
 * Zustand store for template workflow management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  TemplateWorkflowState,
  TemplateWorkflowStepId,
  StepStatus,
  ValidationStatus,
  LayoutGenerationStatus,
  DesignDocumentStatus,
  TemplateWorkflowEventType,
} from '@/types/workflow';

interface TemplateWorkflowStoreState {
  // State - Map of conversationId to template workflow state
  templateWorkflows: Map<string, TemplateWorkflowState>;

  // Actions
  initTemplateWorkflow: (conversationId: string) => void;
  setTemplateWorkflowVisibility: (conversationId: string, visible: boolean) => void;
  setTemplateWorkflowEnabled: (conversationId: string, enabled: boolean) => void;
  updateTemplateStepStatus: (
    conversationId: string,
    step: TemplateWorkflowStepId,
    updates: Partial<StepStatus>
  ) => void;
  updateValidationStatus: (
    conversationId: string,
    updates: Partial<ValidationStatus>
  ) => void;
  updateLayoutGenerationStatus: (
    conversationId: string,
    updates: Partial<LayoutGenerationStatus>
  ) => void;
  updateDesignDocumentStatus: (
    conversationId: string,
    updates: Partial<DesignDocumentStatus>
  ) => void;
  progressToNextTemplateStep: (conversationId: string) => void;
  handleTemplateWorkflowEvent: (conversationId: string, eventType: TemplateWorkflowEventType, data?: any) => void;
  toggleTemplateStepExpanded: (conversationId: string, step: TemplateWorkflowStepId) => void;
  resetTemplateWorkflow: (conversationId: string) => void;
  clearTemplateWorkflow: (conversationId: string) => void;

  // Getters
  getTemplateWorkflowState: (conversationId: string) => TemplateWorkflowState | null;
  isTemplateWorkflowVisible: (conversationId: string) => boolean;
  isTemplateWorkflowEnabled: (conversationId: string) => boolean;
  getCurrentTemplateStep: (conversationId: string) => TemplateWorkflowStepId | null;
  getTemplateStepStatus: (conversationId: string, step: TemplateWorkflowStepId) => StepStatus | null;
}

// Default step statuses for template workflow
const createDefaultValidationStatus = (): ValidationStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: true, // First step is always accessible
  isExpanded: true, // Auto-expand first step
});

const createDefaultLayoutGenerationStatus = (): LayoutGenerationStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false, // Must complete validation first
  isExpanded: false,
  layouts: [],
  totalLayouts: 0,
  completedLayouts: 0,
});

const createDefaultDesignDocumentStatus = (): DesignDocumentStatus => ({
  status: 'pending',
  progress: 0,
  canProceed: false, // Must generate layouts first
  isExpanded: false,
});

// Create default template workflow state
const createDefaultTemplateWorkflowState = (conversationId: string): TemplateWorkflowState => ({
  conversationId,
  isVisible: true, // Show by default
  isEnabled: true, // Enabled by default
  currentStep: 'validation', // Start with validation
  steps: {
    validation: createDefaultValidationStatus(),
    layout_generation: createDefaultLayoutGenerationStatus(),
    design_document: createDefaultDesignDocumentStatus(),
  },
  createdAt: new Date(),
  updatedAt: new Date(),
});

export const useTemplateWorkflowStore = create<TemplateWorkflowStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        templateWorkflows: new Map(),

        // Initialize template workflow for a conversation
        initTemplateWorkflow: (conversationId: string) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            if (!templateWorkflows.has(conversationId)) {
              templateWorkflows.set(conversationId, createDefaultTemplateWorkflowState(conversationId));
            }
            return { templateWorkflows };
          }, false, 'initTemplateWorkflow');
        },

        // Set template workflow visibility (toggle panel)
        setTemplateWorkflowVisibility: (conversationId: string, visible: boolean) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                isVisible: visible,
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'setTemplateWorkflowVisibility');
        },

        // Enable/disable template workflow for conversation
        setTemplateWorkflowEnabled: (conversationId: string, enabled: boolean) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                isEnabled: enabled,
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'setTemplateWorkflowEnabled');
        },

        // Update generic step status
        updateTemplateStepStatus: (
          conversationId: string,
          step: TemplateWorkflowStepId,
          updates: Partial<StepStatus>
        ) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  [step]: {
                    ...workflow.steps[step],
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'updateTemplateStepStatus');
        },

        // Update validation status
        updateValidationStatus: (
          conversationId: string,
          updates: Partial<ValidationStatus>
        ) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  validation: {
                    ...workflow.steps.validation,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'updateValidationStatus');
        },

        // Update layout generation status
        updateLayoutGenerationStatus: (
          conversationId: string,
          updates: Partial<LayoutGenerationStatus>
        ) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  layout_generation: {
                    ...workflow.steps.layout_generation,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'updateLayoutGenerationStatus');
        },

        // Update design document status
        updateDesignDocumentStatus: (
          conversationId: string,
          updates: Partial<DesignDocumentStatus>
        ) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              templateWorkflows.set(conversationId, {
                ...workflow,
                steps: {
                  ...workflow.steps,
                  design_document: {
                    ...workflow.steps.design_document,
                    ...updates,
                  },
                },
                updatedAt: new Date(),
              });
            }
            return { templateWorkflows };
          }, false, 'updateDesignDocumentStatus');
        },

        // Progress to next template step
        progressToNextTemplateStep: (conversationId: string) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            const workflow = templateWorkflows.get(conversationId);
            if (workflow) {
              const stepOrder: TemplateWorkflowStepId[] = [
                'validation',
                'layout_generation',
                'design_document',
              ];
              const currentIndex = stepOrder.indexOf(workflow.currentStep);
              if (currentIndex < stepOrder.length - 1) {
                const nextStep = stepOrder[currentIndex + 1];
                templateWorkflows.set(conversationId, {
                  ...workflow,
                  currentStep: nextStep,
                  updatedAt: new Date(),
                });
              }
            }
            return { templateWorkflows };
          }, false, 'progressToNextTemplateStep');
        },

        // Handle template workflow event
        handleTemplateWorkflowEvent: (conversationId: string, eventType: TemplateWorkflowEventType, data?: any) => {
          switch (eventType) {
            case 'template_workflow_initialized':
              get().initTemplateWorkflow(conversationId);
              get().setTemplateWorkflowEnabled(conversationId, true);
              break;

            case 'validation_started':
              get().updateValidationStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
                jobId: data?.jobId,
              });
              break;

            case 'validation_progress':
              get().updateValidationStatus(conversationId, {
                progress: data?.progress || 0,
              });
              break;

            case 'validation_completed':
              get().updateValidationStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                validationPath: data?.validationPath,
                layoutCount: data?.layoutCount,
                totalAssets: data?.totalAssets,
                assetsByType: data?.assetsByType,
                assets: data?.assets,
                recommendations: data?.recommendations,
              });
              // Enable layout generation step
              get().updateLayoutGenerationStatus(conversationId, {
                canProceed: true,
                totalLayouts: data?.layoutCount || 0,
              });
              break;

            case 'validation_error':
              get().updateValidationStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Validation failed',
              });
              break;

            case 'layout_generation_started':
              get().updateLayoutGenerationStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
                jobId: data?.jobId,
              });
              get().progressToNextTemplateStep(conversationId);
              break;

            case 'layout_generation_progress':
              get().updateLayoutGenerationStatus(conversationId, {
                progress: data?.progress || 0,
                currentLayout: data?.currentLayout,
                layouts: data?.layouts,
                completedLayouts: data?.completedLayouts,
              });
              break;

            case 'layout_completed':
              const workflow = get().getTemplateWorkflowState(conversationId);
              if (workflow) {
                const layoutGenStatus = workflow.steps.layout_generation;
                const updatedLayouts = [...(layoutGenStatus.layouts || [])];
                const layoutIndex = updatedLayouts.findIndex(
                  (l) => l.id === data?.layoutId
                );
                if (layoutIndex >= 0) {
                  updatedLayouts[layoutIndex] = {
                    ...updatedLayouts[layoutIndex],
                    status: 'complete',
                    ...data,
                  };
                } else {
                  updatedLayouts.push({
                    id: data?.layoutId,
                    name: data?.layoutName,
                    htmlPath: data?.htmlPath,
                    jsonPath: data?.jsonPath,
                    status: 'complete',
                    metadata: data?.metadata,
                  });
                }
                get().updateLayoutGenerationStatus(conversationId, {
                  layouts: updatedLayouts,
                  completedLayouts: updatedLayouts.filter((l) => l.status === 'complete').length,
                });
              }
              break;

            case 'layout_generation_completed':
              get().updateLayoutGenerationStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                layouts: data?.layouts,
              });
              // Enable design document step
              get().updateDesignDocumentStatus(conversationId, {
                canProceed: true,
              });
              break;

            case 'layout_generation_error':
              get().updateLayoutGenerationStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Layout generation failed',
              });
              break;

            case 'design_document_started':
              get().updateDesignDocumentStatus(conversationId, {
                status: 'in_progress',
                progress: 0,
                startedAt: new Date(),
                isExpanded: true,
                jobId: data?.jobId,
              });
              get().progressToNextTemplateStep(conversationId);
              break;

            case 'design_document_progress':
              get().updateDesignDocumentStatus(conversationId, {
                progress: data?.progress || 0,
              });
              break;

            case 'design_document_completed':
              get().updateDesignDocumentStatus(conversationId, {
                status: 'completed',
                progress: 100,
                completedAt: new Date(),
                documentPath: data?.documentPath,
                sections: data?.sections,
                wordCount: data?.wordCount,
                createdAt: new Date(),
              });
              break;

            case 'design_document_error':
              get().updateDesignDocumentStatus(conversationId, {
                status: 'error',
                error: data?.error || 'Design document generation failed',
              });
              break;

            case 'template_workflow_completed':
              // Mark all steps as completed
              get().updateValidationStatus(conversationId, { status: 'completed' });
              get().updateLayoutGenerationStatus(conversationId, { status: 'completed' });
              get().updateDesignDocumentStatus(conversationId, { status: 'completed' });
              break;

            case 'template_workflow_error':
              const currentWorkflow = get().getTemplateWorkflowState(conversationId);
              if (currentWorkflow) {
                get().updateTemplateStepStatus(conversationId, currentWorkflow.currentStep, {
                  status: 'error',
                  error: data?.error || 'Template workflow error occurred',
                });
              }
              break;
          }
        },

        // Toggle step expansion
        toggleTemplateStepExpanded: (conversationId: string, step: TemplateWorkflowStepId) => {
          const workflow = get().getTemplateWorkflowState(conversationId);
          if (workflow) {
            const currentStatus = workflow.steps[step];
            get().updateTemplateStepStatus(conversationId, step, {
              isExpanded: !currentStatus.isExpanded,
            });
          }
        },

        // Reset template workflow to initial state
        resetTemplateWorkflow: (conversationId: string) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            templateWorkflows.set(conversationId, createDefaultTemplateWorkflowState(conversationId));
            return { templateWorkflows };
          }, false, 'resetTemplateWorkflow');
        },

        // Clear template workflow from store
        clearTemplateWorkflow: (conversationId: string) => {
          set((state) => {
            const templateWorkflows = new Map(state.templateWorkflows);
            templateWorkflows.delete(conversationId);
            return { templateWorkflows };
          }, false, 'clearTemplateWorkflow');
        },

        // Getters
        getTemplateWorkflowState: (conversationId: string) => {
          return get().templateWorkflows.get(conversationId) || null;
        },

        isTemplateWorkflowVisible: (conversationId: string) => {
          const workflow = get().templateWorkflows.get(conversationId);
          return workflow?.isVisible || false;
        },

        isTemplateWorkflowEnabled: (conversationId: string) => {
          const workflow = get().templateWorkflows.get(conversationId);
          return workflow?.isEnabled || false;
        },

        getCurrentTemplateStep: (conversationId: string) => {
          const workflow = get().templateWorkflows.get(conversationId);
          return workflow?.currentStep || null;
        },

        getTemplateStepStatus: (conversationId: string, step: TemplateWorkflowStepId) => {
          const workflow = get().templateWorkflows.get(conversationId);
          return workflow?.steps[step] || null;
        },
      }),
      {
        name: 'template-workflow-store',
        // Custom serializer to handle Map and Date objects
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            if (!str) return null;
            const { state } = JSON.parse(str);

            // Convert template workflow entries and restore Date objects
            const templateWorkflowEntries = Object.entries(state.templateWorkflows || {}).map(([key, workflow]: [string, any]): [string, TemplateWorkflowState] => {
              return [
                key,
                {
                  ...workflow,
                  createdAt: workflow.createdAt ? new Date(workflow.createdAt) : undefined,
                  updatedAt: workflow.updatedAt ? new Date(workflow.updatedAt) : undefined,
                  steps: {
                    validation: {
                      ...workflow.steps.validation,
                      startedAt: workflow.steps.validation.startedAt
                        ? new Date(workflow.steps.validation.startedAt)
                        : undefined,
                      completedAt: workflow.steps.validation.completedAt
                        ? new Date(workflow.steps.validation.completedAt)
                        : undefined,
                    },
                    layout_generation: {
                      ...workflow.steps.layout_generation,
                      startedAt: workflow.steps.layout_generation.startedAt
                        ? new Date(workflow.steps.layout_generation.startedAt)
                        : undefined,
                      completedAt: workflow.steps.layout_generation.completedAt
                        ? new Date(workflow.steps.layout_generation.completedAt)
                        : undefined,
                    },
                    design_document: {
                      ...workflow.steps.design_document,
                      startedAt: workflow.steps.design_document.startedAt
                        ? new Date(workflow.steps.design_document.startedAt)
                        : undefined,
                      completedAt: workflow.steps.design_document.completedAt
                        ? new Date(workflow.steps.design_document.completedAt)
                        : undefined,
                      createdAt: workflow.steps.design_document.createdAt
                        ? new Date(workflow.steps.design_document.createdAt)
                        : undefined,
                    },
                  },
                } as TemplateWorkflowState,
              ];
            });

            return {
              state: {
                ...state,
                templateWorkflows: new Map<string, TemplateWorkflowState>(templateWorkflowEntries),
              },
            };
          },
          setItem: (name, value) => {
            const templateWorkflows = Object.fromEntries(value.state.templateWorkflows);
            localStorage.setItem(
              name,
              JSON.stringify({
                state: {
                  ...value.state,
                  templateWorkflows,
                },
              })
            );
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      }
    ),
    {
      name: 'template-workflow-store',
    }
  )
);

// Expose to window for console access during development
if (typeof window !== 'undefined') {
  (window as any).useTemplateWorkflowStore = useTemplateWorkflowStore;
  (window as any).templateWorkflowStore = useTemplateWorkflowStore.getState();
}
